/**
 * The numbers and words in an AI proposal you can fix before saving: if it says $40 an hour,
 * type 50 and confirm. Works on the proposed commands themselves, so what you see is exactly
 * what gets saved.
 */
import type { Command } from "../commands.ts";

export type FieldKind = "money" | "percent" | "text" | "date";

export interface EditableField {
  /** Which command in the proposal (index) and which field. */
  key: string;
  label: string;
  kind: FieldKind;
  /** The value as you'd type it: "40.00", "7.75", "Zelle", "2026-10-12". */
  value: string;
}

const money = (c: number) => (Math.abs(c) / 100).toFixed(2);
const pct = (bp: number) => String(bp / 100);

/** Editable fields for every command in a proposal. */
export function proposalFields(commands: readonly Command[]): EditableField[] {
  const out: EditableField[] = [];
  commands.forEach((cmd, i) => {
    const k = (f: string) => `${i}.${f}`;
    const many = commands.length > 1;
    switch (cmd.type) {
      case "item.add": {
        const f = cmd.fields;
        const tag = many ? ` (${f.name})` : "";
        out.push({ key: k("name"), label: "Description", kind: "text", value: f.name });
        out.push({
          key: k("price"),
          label: `Price${tag}`,
          kind: "money",
          value: money(f.priceCents),
        });
        if (f.couponCents)
          out.push({
            key: k("coupon"),
            label: `Coupon${tag}`,
            kind: "money",
            value: money(f.couponCents),
          });
        if (f.taxBp !== null)
          out.push({ key: k("tax"), label: `Tax %${tag}`, kind: "percent", value: pct(f.taxBp) });
        out.push({ key: k("date"), label: `Date${tag}`, kind: "date", value: f.date });
        break;
      }
      case "payment.add":
        out.push({ key: k("amount"), label: "Amount", kind: "money", value: money(cmd.cents) });
        out.push({ key: k("note"), label: "Note", kind: "text", value: cmd.note ?? "" });
        out.push({ key: k("date"), label: "Date", kind: "date", value: cmd.at.slice(0, 10) });
        break;
      case "receipt.set":
        if (cmd.cents !== null)
          out.push({
            key: k("total"),
            label: "Receipt total",
            kind: "money",
            value: money(cmd.cents),
          });
        break;
    }
  });
  return out;
}

function readCents(text: string): number | null {
  const t = text.replace(/[$,\s]/g, "");
  if (!/^\d+(\.\d{1,2})?$|^\.\d{1,2}$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

function readBp(text: string): number | null {
  const t = text.replace(/[%\s]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

/**
 * The proposal with your edits applied. Returns an error message instead when an edit
 * can't be read (like "abc" for a price), so nothing half-fixed is saved.
 */
export function applyFieldEdits(
  commands: readonly Command[],
  edits: Record<string, string>,
): { ok: true; commands: Command[] } | { ok: false; message: string } {
  const out = structuredClone(commands) as Command[];
  for (const [key, raw] of Object.entries(edits)) {
    const [iText, field] = key.split(".");
    const cmd = out[Number(iText)];
    if (!cmd || !field) continue;
    const text = raw.trim();
    if (field === "date" && !/^\d{4}-\d{2}-\d{2}$/.test(text))
      return { ok: false, message: "Dates look like 2026-10-12" };
    if (cmd.type === "item.add") {
      if (field === "name") {
        if (!text) return { ok: false, message: "The description can't be empty" };
        cmd.fields.name = text;
      } else if (field === "price" || field === "coupon") {
        const c = readCents(text);
        if (c === null) return { ok: false, message: `Enter the ${field} like 40.00` };
        if (field === "price") cmd.fields.priceCents = c;
        else cmd.fields.couponCents = -c;
      } else if (field === "tax") {
        const bp = readBp(text);
        if (bp === null || bp > 10_000) return { ok: false, message: "Enter tax like 7.75" };
        cmd.fields.taxBp = bp;
      } else if (field === "date") cmd.fields.date = text;
    } else if (cmd.type === "payment.add") {
      if (field === "amount") {
        const c = readCents(text);
        if (!c) return { ok: false, message: "Enter the amount like 40.00" };
        cmd.cents = cmd.cents < 0 ? -c : c;
      } else if (field === "note") cmd.note = text;
      else if (field === "date") cmd.at = `${text}${cmd.at.slice(10)}`;
    } else if (cmd.type === "receipt.set" && field === "total") {
      const c = readCents(text);
      if (c === null) return { ok: false, message: "Enter the total like 622.90" };
      cmd.cents = c;
    }
  }
  return { ok: true, commands: out };
}
